import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import ConditionalRenderer from './ConditionalRenderer';
import { AxiosError } from 'axios';
import { imagesQueryKeys } from '@/data/services/querykeys';
import { dropdown, fadeInOut } from '@/utils/animations/commom';
import { MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import { preventDefault } from '@/utils/mouseEvent';
import { twMerge } from 'tailwind-merge';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
interface AstroImageValidationProps {
  renderLoading?: React.FC;
  timeout?: number;
}
interface AstroImageProps
  extends React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  > {
  alternativesrc?: string;
  validation?: AstroImageValidationProps;
  zoomable?: boolean;
  classNames?: Pick<ClassNameComponent, 'button' | 'img'>;
}

export default function AstroImage({
  zoomable,
  classNames,
  className,
  ...props
}: AstroImageProps) {
  const imgClassName = twMerge(className, classNames?.img);

  if (zoomable) {
    return (
      <ZoomableAstroImage
        classNames={{ ...classNames, img: imgClassName }}
        {...props}
      />
    );
  }
  return <AstroImageBase className={imgClassName} {...props} />;
}

function AstroImageBase({
  validation,
  alternativesrc = '/images/img-placeholder.png',
  alt = 'Imagem na plataforma Astro',
  ...props
}: AstroImageProps) {
  if (validation) {
    return (
      <AstroImageWithValidation
        validation={validation}
        alternativesrc={alternativesrc}
        alt={alt}
        {...props}
      />
    );
  }
  return (
    <AstroImageWithoutValidation
      alternativesrc={alternativesrc}
      alt={alt}
      {...props}
    />
  );
}

type ZoomableAstroImageProps = Omit<AstroImageProps, 'zoomable' | 'className'>;

function ZoomableAstroImage({
  id,
  classNames,
  ...props
}: ZoomableAstroImageProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'imageZoom' });
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = () => {
    dialogRef.current?.showModal();
    setIsOpen(true);
  };

  const close = () => setIsOpen(false);

  return (
    <Fragment>
      <button
        type="button"
        aria-label={t('expand')}
        onClick={e => {
          preventDefault(e);
          open();
        }}
        className={twMerge(
          'group relative inline-flex cursor-zoom-in overflow-hidden rounded',
          classNames?.button,
        )}
      >
        <AstroImageBase id={id} className={classNames?.img} {...props} />
        <span className="absolute inset-0 flex items-center justify-center rounded bg-black/0 opacity-0 transition duration-200 group-hover:bg-black/50 group-hover:opacity-100">
          <MagnifyingGlassPlusIcon className="h-8 w-8 text-white" />
        </span>
      </button>
      <dialog
        ref={dialogRef}
        aria-label={t('expand')}
        onCancel={event => {
          event.preventDefault();
          close();
        }}
        className="fixed inset-0 m-0 h-full max-h-none w-full max-w-none border-0 bg-transparent p-0 backdrop:bg-transparent"
      >
        <AnimatePresence onExitComplete={() => dialogRef.current?.close()}>
          {isOpen && (
            <motion.div
              key="zoom-overlay"
              onClick={close}
              {...fadeInOut}
              className="flex h-full w-full cursor-zoom-out items-center justify-center bg-black/70 p-4"
            >
              <motion.div {...dropdown}>
                <AstroImageBase
                  {...props}
                  className="h-[50vh] max-w-[82vw] min-w-[200px] w-full object-contain"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </dialog>
    </Fragment>
  );
}

type AstroImageWithoutValidationProps = AstroImageProps &
  Required<Pick<AstroImageProps, 'alternativesrc' | 'alt'>>;

function AstroImageWithoutValidation({
  id,
  alt,
  alternativesrc,
  src: urlImg,
  ...props
}: AstroImageWithoutValidationProps) {
  const [src, setSrc] = useState<string>(urlImg || alternativesrc);

  useEffect(() => {
    setSrc(urlImg || alternativesrc);
  }, [urlImg, alternativesrc]);

  return (
    <img
      id={id}
      alt={alt}
      src={src}
      onError={() => setSrc(alternativesrc)}
      {...props}
    />
  );
}

type AstroImageWithValidationProps = AstroImageProps &
  Required<Pick<AstroImageProps, 'alternativesrc' | 'validation' | 'alt'>>;

function AstroImageWithValidation({
  validation,
  src: urlImg,
  ...props
}: AstroImageWithValidationProps) {
  const [src, setSrc] = useState<string>(urlImg || props.alternativesrc);
  const { renderLoading: LoadingComponent, timeout } = validation;
  const {
    isInitialLoading: isLoading,
    isError,
    isSuccess,
  } = useQuery({
    ...imagesQueryKeys.get(urlImg ?? '', timeout),
    enabled: !!urlImg,
    retry: (failureCount, error) => {
      return (
        error instanceof AxiosError &&
        error.code === AxiosError.ERR_NETWORK &&
        failureCount < 3
      );
    },
  });

  useEffect(() => {
    let mounted = true;
    if (isError && mounted) {
      setSrc(props.alternativesrc);
    }
    return () => {
      mounted = false;
    };
  }, [isError, urlImg, props.alternativesrc]);

  useEffect(() => {
    let mounted = true;
    if (isSuccess && mounted) {
      setSrc(urlImg as string);
    }
    return () => {
      mounted = false;
    };
  }, [isSuccess, urlImg]);

  if (isLoading) {
    return (
      <ConditionalRenderer condition={LoadingComponent}>
        {LoadingComponent && <LoadingComponent />}
      </ConditionalRenderer>
    );
  }

  return (
    <img
      {...props}
      alt={props.alt}
      src={src}
      onError={() => setSrc(props.alternativesrc)}
    />
  );
}
